import type { AssetContainer } from '@babylonjs/core/assetContainer'
import type { Scene } from '@babylonjs/core/scene'
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader'
import { ASSET_MANIFEST, type AssetFallback, type ModelPackDefinition } from './AssetManifest'

export interface LoadedAssetPack {
  readonly definition: ModelPackDefinition
  readonly container: AssetContainer | null
  readonly fallback: AssetFallback
  readonly error?: string
}

export class AssetLoader {
  private readonly scene: Scene
  private readonly packs = new Map<string, LoadedAssetPack>()

  public constructor(scene: Scene) {
    this.scene = scene
  }

  public get(id: string): LoadedAssetPack | undefined {
    return this.packs.get(id)
  }

  public async preload(): Promise<readonly LoadedAssetPack[]> {
    await import('@babylonjs/loaders/glTF')
    const loaded = await Promise.all(ASSET_MANIFEST.packs.filter((pack) => pack.preload).map(async (definition) => {
      try {
        const container = await LoadAssetContainerAsync(definition.url, this.scene)
        const result: LoadedAssetPack = { definition, container, fallback: definition.fallback }
        this.packs.set(definition.id, result)
        return result
      } catch (error) {
        const result: LoadedAssetPack = {
          definition,
          container: null,
          fallback: definition.fallback,
          error: error instanceof Error ? error.message : 'Asset loading failed',
        }
        this.packs.set(definition.id, result)
        return result
      }
    }))
    return loaded
  }

  public dispose(): void {
    for (const pack of this.packs.values()) pack.container?.dispose()
    this.packs.clear()
  }
}
